import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import useChallengeStore from '../hooks/useChallenge'
import { Calendar, Clock, Target, Eye, TrendingUp, Award, BarChart3, Filter, X } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/errorUtils.js'
import availableStrategiesService from '../services/availableStrategiesService'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'

const History = () => {
  const { user } = useAuthStore()
  const { getChallengeHistory, formatTime, isLoading } = useChallengeStore()

  // Redirect guardians - they shouldn't access challenge history
  if (user?.accountType === 'guardian') {
    return <Navigate to="/dashboard" replace />
  }
  const [sessions, setSessions] = useState([])
  const [pagination, setPagination] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Filter states
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterStrategy, setFilterStrategy] = useState('all')
  const [filterDateRange, setFilterDateRange] = useState('all')
  const [availableStrategies, setAvailableStrategies] = useState([])

  useEffect(() => {
    loadHistory()
  }, [currentPage])

  useEffect(() => {
    loadAvailableStrategies()
  }, [])

  const loadAvailableStrategies = async () => {
    const strategies = await availableStrategiesService.getAvailableStrategies()
    setAvailableStrategies(strategies)
  }

  const loadHistory = async () => {
    if (currentPage === 1) {
      setIsLoadingMore(false)
    } else {
      setIsLoadingMore(true)
    }

    const result = await getChallengeHistory(currentPage, 10)
    if (result.success) {
      if (currentPage === 1) {
        setSessions(result.sessions)
      } else {
        setSessions((prev) => [...prev, ...result.sessions])
      }
      setPagination(result.pagination)
    } else {
      toast.error(getErrorMessage(result.error))
    }

    setIsLoadingMore(false)
  }

  const loadMore = () => {
    if (currentPage < pagination.pages) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  // Filter sessions based on selected filters
  const filteredSessions = useMemo(() => {
    let filtered = sessions

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((s) => s.status === filterStatus)
    }

    // Filter by strategy
    if (filterStrategy !== 'all') {
      filtered = filtered.filter((s) => s.strategyType === filterStrategy)
    }

    // Filter by date range
    if (filterDateRange !== 'all') {
      const now = new Date()
      filtered = filtered.filter((s) => {
        const sessionDate = new Date(s.startedAt)
        const diffDays = Math.floor((now - sessionDate) / (1000 * 60 * 60 * 24))

        switch (filterDateRange) {
          case 'today':
            return diffDays === 0
          case 'week':
            return diffDays <= 7
          case 'month':
            return diffDays <= 30
          case '3months':
            return diffDays <= 90
          default:
            return true
        }
      })
    }

    return filtered
  }, [sessions, filterStatus, filterStrategy, filterDateRange])

  // Calculate summary statistics based on filtered sessions
  const stats = useMemo(() => {
    const completedSessions = filteredSessions.filter((s) => s.status === 'completed')
    const validSessions = completedSessions.filter(
      (s) => s.totalScore !== null && s.totalScore !== undefined && !isNaN(s.totalScore)
    )

    const avgScore =
      validSessions.length > 0
        ? validSessions.reduce((sum, s) => sum + s.totalScore, 0) / validSessions.length
        : 0

    const bestScore =
      validSessions.length > 0 ? Math.max(...validSessions.map((s) => s.totalScore)) : 0

    const totalTime = filteredSessions.reduce((sum, s) => sum + (s.totalTime || 0), 0)

    return {
      total: filteredSessions.length,
      completed: completedSessions.length,
      avgScore: Math.round(avgScore * 10) / 10,
      bestScore: Math.round(bestScore * 10) / 10,
      totalTime
    }
  }, [filteredSessions])

  // Check if any filters are active
  const hasActiveFilters =
    filterStatus !== 'all' || filterStrategy !== 'all' || filterDateRange !== 'all'

  // Fill the viewport so the sessions list scrolls internally (header/stats/filters stay pinned
  // above, "Load More" pinned below) instead of leaving an empty band under a short list.
  const listFillRef = useRef(null)
  const listFillHeight = useFillViewportHeight(listFillRef, {
    enabled: filteredSessions.length > 0
  })

  // Clear all filters
  const clearFilters = () => {
    setFilterStatus('all')
    setFilterStrategy('all')
    setFilterDateRange('all')
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success-600 dark:text-success-400'
    if (score >= 60) return 'text-warning-600 dark:text-warning-400'
    return 'text-error-600 dark:text-error-400'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success-600 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
      case 'cancelled':
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Challenge History</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          View your past challenge sessions and track your progress
        </p>
      </div>

      {/* Summary Statistics - Top Bar */}
      {sessions.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="card-body p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                    {stats.total}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Challenges</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success-100 dark:bg-success-900/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-success-600 dark:text-success-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                    {stats.avgScore}%
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Average Score</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                    {stats.bestScore}%
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Best Score</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
                    {formatTime(stats.totalTime)}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {sessions.length > 0 && (
        <div className="card">
          <div className="card-body p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>

              <div className="flex flex-wrap items-center gap-3 flex-1">
                {/* Status Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {/* Strategy Filter */}
                <select
                  value={filterStrategy}
                  onChange={(e) => setFilterStrategy(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Strategies</option>
                  {availableStrategies.map((strategy) => (
                    <option
                      key={strategy.strategyType || strategy.type}
                      value={strategy.strategyType || strategy.type}
                    >
                      {strategy.name}
                    </option>
                  ))}
                </select>

                {/* Date Range Filter */}
                <select
                  value={filterDateRange}
                  onChange={(e) => setFilterDateRange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="3months">Last 3 Months</option>
                </select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sessions List + Load More — filled to the viewport so the list scrolls internally while
          the "Load More" control stays pinned at the bottom. */}
      <div
        ref={listFillRef}
        className="flex flex-col gap-3"
        style={listFillHeight ? { height: listFillHeight } : undefined}
      >
        <div className="space-y-3 flex-1 overflow-auto min-h-0">
          {filteredSessions.map((session) => (
            <div key={session.id} className="card hover:shadow-md transition-shadow duration-200">
              <div className="card-body p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Left: Title and metadata */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-2">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate flex-1">
                        {session.name || 'Challenge'}
                      </h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(session.status)}`}
                      >
                        {session.status}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(session.startedAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatTime(session.totalTime || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle: Scores */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="text-center">
                      <p className={`text-lg font-bold ${getScoreColor(session.totalScore || 0)}`}>
                        {session.totalScore !== null ? Math.round(session.totalScore) : 0}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Overall</p>
                    </div>

                    {session.status === 'completed' && (
                      <>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {session.exploratoryScore !== null
                              ? Math.round(session.exploratoryScore)
                              : 0}
                            %
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Exploratory</p>
                        </div>

                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {session.adaptiveScore !== null ? Math.round(session.adaptiveScore) : 0}
                            %
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Adaptive</p>
                        </div>

                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {session.startingLevel || 1} → {session.endingLevel || 1}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Level</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Right: Action button */}
                  {session.status === 'completed' && (
                    <Link
                      to={`/challenge/${session.id}/report`}
                      className="btn-secondary text-sm px-3 py-2 flex items-center gap-2 whitespace-nowrap"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">View Report</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More — pinned below the scrolling list inside the fill region. */}
        {currentPage < pagination.pages && (
          <div className="text-center shrink-0">
            <button onClick={loadMore} disabled={isLoadingMore} className="btn-secondary">
              {isLoadingMore ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>Loading...</span>
                </div>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Empty State - No sessions at all */}
      {sessions.length === 0 && !isLoading && (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No challenge history
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You haven't taken any challenges yet. Start your first challenge to see your progress
              here.
            </p>
            <Link to="/challenge-type" className="btn-primary inline-flex">
              Start Your First Challenge
            </Link>
          </div>
        </div>
      )}

      {/* Empty State - No filtered results */}
      {sessions.length > 0 && filteredSessions.length === 0 && !isLoading && (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No challenges match your filters
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Try adjusting your filters to see more results.
            </p>
            <button onClick={clearFilters} className="btn-secondary inline-flex">
              Clear Filters
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default History
